import {
    Vec3
} from 'playcanvas';

import type { Annotation, AnnotationLayer } from './annotations';
import { Element, ElementType } from './element';
import annotationDamageTypeSvg from './ui/svg/annotation-damage-type.svg';
import annotationEvidenceTypeSvg from './ui/svg/annotation-evidence-type.svg';
import annotationPinTypeSvg from './ui/svg/annotation-pin-type.svg';
import annotationPoiTypeSvg from './ui/svg/annotation-poi-type.svg';
import annotationVehicleTypeSvg from './ui/svg/annotation-vehicle-type.svg';
import annotationWitnessTypeSvg from './ui/svg/annotation-witness-type.svg';

const TYPE_SVG_ICONS: Record<Annotation['type'], string> = {
    pin: annotationPinTypeSvg,
    evidence: annotationEvidenceTypeSvg,
    witness: annotationWitnessTypeSvg,
    vehicle: annotationVehicleTypeSvg,
    poi: annotationPoiTypeSvg,
    damage: annotationDamageTypeSvg
};

const TYPE_CSS_COLORS: Record<Annotation['type'], string> = {
    pin: '#4AB0F2',
    evidence: '#FACC3D',
    witness: '#B57DED',
    vehicle: '#30C48C',
    poi: '#ED7840',
    damage: '#ED3D52'
};

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

type MarkerState = {
    id: string,
    type: Annotation['type'],
    position: Vec3,
    label: string,
    exhibitId?: string
};

type LabelOverlayState = {
    id: string,
    root: HTMLDivElement,
    leader: SVGLineElement,
    icon: HTMLElement,
    text: HTMLSpanElement,
    badge: HTMLSpanElement
};

const tmpCamPos = new Vec3();
const tmpScreen = new Vec3();
const tmpToPoint = new Vec3();

class AnnotationMarkers extends Element {
    markers = new Map<string, MarkerState>();
    overlayRoot: HTMLDivElement | null = null;
    leaderSvg: SVGSVGElement | null = null;
    labels = new Map<string, LabelOverlayState>();

    offscreenMargin = 72;
    canvasDeselect: (() => void) | null = null;

    dirtyMarkers = true;
    dirtySelection = true;
    previousSelectedId = '';

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const { scene } = this;

        this.createOverlayRoot();

        const markMarkersDirty = () => {
            this.dirtyMarkers = true;
            scene.forceRender = true;
        };

        const markSelectionDirty = (nextSelectedId?: string | null) => {
            this.dirtySelection = true;
            try {
                const selectedId = typeof nextSelectedId === 'string' ? nextSelectedId : String(scene.events.invoke('annotations.selected') ?? '');
                this.refreshSelectionStyling(selectedId);
            } catch (e) {
                // best effort
            }
            scene.forceRender = true;
        };

        const { events } = scene;
        events.on('annotations.listChanged', markMarkersDirty);
        events.on('annotations.layersChanged', markMarkersDirty);
        events.on('annotations.selectedChanged', markSelectionDirty);
        events.on('annotations.select', (id: string | null) => {
            markSelectionDirty(typeof id === 'string' ? id : null);
        });
        events.on('scene.clear', markMarkersDirty);

        // Hover event from panel UI — toggle CSS class on 3D label overlay
        events.on('annotations.hover', (id: string | null) => {
            for (const entry of this.labels.values()) {
                entry.root.classList.toggle('hovered', entry.id === id);
            }
            scene.forceRender = true;
        });

        // Click on empty canvas deselects annotation
        this.canvasDeselect = () => {
            const selectedId = scene.events.invoke('annotations.selected');
            if (selectedId) {
                scene.events.fire('annotations.select', null);
            }
        };
        scene.canvas.addEventListener('pointerdown', this.canvasDeselect);
    }

    remove() {
        if (this.canvasDeselect) {
            this.scene.canvas.removeEventListener('pointerdown', this.canvasDeselect);
            this.canvasDeselect = null;
        }
        this.clearLabels();
        this.overlayRoot?.remove();
        this.overlayRoot = null;
        this.leaderSvg = null;

        this.markers.clear();
    }

    onPreRender() {
        if (this.dirtyMarkers) {
            this.dirtyMarkers = false;
            this.rebuildMarkers();
            this.dirtySelection = true;
        }

        const selectedId = String(this.scene.events.invoke('annotations.selected') ?? '');
        if (selectedId !== this.previousSelectedId) {
            this.previousSelectedId = selectedId;
            this.dirtySelection = true;
        }

        if (this.dirtySelection) {
            this.dirtySelection = false;
            this.refreshSelectionStyling(selectedId);
        }
    }

    onPostRender() {
        const selectedId = String(this.scene.events.invoke('annotations.selected') ?? '');
        this.updateLabelOverlay(selectedId);
    }

    private clearLabels() {
        for (const label of this.labels.values()) {
            label.root.remove();
            label.leader.remove();
        }
        this.labels.clear();
    }

    private createOverlayRoot() {
        if (this.overlayRoot) {
            return;
        }

        const canvasContainer = window.document.getElementById('canvas-container');
        if (!canvasContainer) {
            return;
        }

        const overlayRoot = window.document.createElement('div');
        overlayRoot.id = 'annotation-labels-overlay';

        const leaderSvg = window.document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        leaderSvg.id = 'annotation-label-leaders';
        leaderSvg.setAttribute('width', '100%');
        leaderSvg.setAttribute('height', '100%');
        leaderSvg.setAttribute('viewBox', '0 0 1 1');
        overlayRoot.appendChild(leaderSvg);

        canvasContainer.appendChild(overlayRoot);

        this.overlayRoot = overlayRoot;
        this.leaderSvg = leaderSvg;
    }

    private createLabelOverlay(marker: MarkerState) {
        if (!this.overlayRoot) {
            return;
        }

        if (!this.leaderSvg) {
            return;
        }

        const leader = window.document.createElementNS('http://www.w3.org/2000/svg', 'line');
        leader.classList.add('annotation-label-leader');
        (leader as any).dataset.annotationId = marker.id;
        this.leaderSvg.appendChild(leader);

        const root = window.document.createElement('div');
        root.className = 'annotation-label';
        root.dataset.annotationId = marker.id;
        root.dataset.annotationType = marker.type;

        const iconSvg = createSvg(TYPE_SVG_ICONS[marker.type] ?? TYPE_SVG_ICONS.pin);
        iconSvg.classList.add('annotation-label-icon');
        iconSvg.style.color = TYPE_CSS_COLORS[marker.type] ?? TYPE_CSS_COLORS.pin;

        const text = window.document.createElement('span');
        text.className = 'annotation-label-text';

        const badge = window.document.createElement('span');
        badge.className = 'annotation-label-exhibit';

        root.appendChild(iconSvg);
        root.appendChild(text);
        root.appendChild(badge);

        root.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.scene.events.fire('annotations.select', marker.id);
        });

        this.overlayRoot.appendChild(root);

        this.labels.set(marker.id, {
            id: marker.id,
            root,
            leader,
            icon: iconSvg,
            text,
            badge
        });

        this.updateLabelContent(marker.id, marker.label, marker.exhibitId);
    }

    private updateLabelContent(id: string, label: string, exhibitId?: string) {
        const entry = this.labels.get(id);
        if (!entry) {
            return;
        }

        const trimmedLabel = (label ?? '').trim();
        entry.text.textContent = trimmedLabel || 'Annotation';

        const exhibitText = (exhibitId ?? '').trim();
        entry.badge.textContent = exhibitText;
        entry.badge.style.display = exhibitText ? 'inline-flex' : 'none';
    }

    private setLabelVisible(id: string, visible: boolean) {
        const entry = this.labels.get(id);
        if (!entry) {
            return;
        }

        entry.root.style.display = visible ? 'inline-flex' : 'none';
        entry.leader.style.display = visible ? 'block' : 'none';
    }

    private annotationVisible(annotation: Annotation, layerVisibility: Map<string, boolean>) {
        if (!annotation.layerId) {
            return true;
        }
        return layerVisibility.get(annotation.layerId) === true;
    }

    private rebuildMarkers() {
        this.markers.clear();
        this.clearLabels();

        const annotations = (this.scene.events.invoke('annotations.list') as Annotation[]) ?? [];
        const layers = (this.scene.events.invoke('annotations.layers') as AnnotationLayer[]) ?? [];
        const layerVisibility = new Map<string, boolean>(layers.map(layer => [layer.id, !!layer.visible]));

        for (const annotation of annotations) {
            if (!annotation?.id || !Array.isArray(annotation.worldPosition) || annotation.worldPosition.length < 3) {
                continue;
            }
            if (!this.annotationVisible(annotation, layerVisibility)) {
                continue;
            }

            const id = String(annotation.id);
            const position = new Vec3(annotation.worldPosition[0], annotation.worldPosition[1], annotation.worldPosition[2]);

            const marker: MarkerState = {
                id,
                type: annotation.type,
                position,
                label: annotation.label,
                exhibitId: annotation.exhibitId
            };

            this.markers.set(id, marker);
            this.createLabelOverlay(marker);
        }
    }

    private refreshSelectionStyling(selectedId: string) {
        for (const marker of this.markers.values()) {
            const entry = this.labels.get(marker.id);
            entry?.root.classList.toggle('selected', marker.id === selectedId);
        }
    }

    private updateLabelOverlay(selectedId: string) {
        if (!this.overlayRoot) {
            return;
        }

        if (!this.leaderSvg) {
            return;
        }

        const canvasRect = this.scene.canvas.getBoundingClientRect();
        const width = Math.max(1, canvasRect.width);
        const height = Math.max(1, canvasRect.height);

        // Update leader SVG viewBox to pixel coordinates so we can assign pixel values directly.
        this.leaderSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);
        const cameraPos = tmpCamPos.copy(this.scene.camera.entity.getPosition());
        const cameraForward = this.scene.camera.entity.forward;
        const isOrtho = this.scene.camera.ortho;

        for (const marker of this.markers.values()) {
            this.updateLabelContent(marker.id, marker.label, marker.exhibitId);

            const entry = this.labels.get(marker.id);
            if (!entry) {
                continue;
            }

            const toPoint = tmpToPoint.sub2(marker.position, cameraPos);
            const behindCamera = !isOrtho && toPoint.dot(cameraForward) <= 0;

            this.scene.camera.worldToScreen(marker.position, tmpScreen);
            const markerX = tmpScreen.x * width;
            const markerY = tmpScreen.y * height;

            const offscreen = tmpScreen.x < -0.02 || tmpScreen.x > 1.02 || tmpScreen.y < -0.02 || tmpScreen.y > 1.02 ||
                markerX < -this.offscreenMargin || markerX > (width + this.offscreenMargin) ||
                markerY < -this.offscreenMargin || markerY > (height + this.offscreenMargin);

            if (behindCamera || offscreen || Number.isNaN(markerX) || Number.isNaN(markerY)) {
                this.setLabelVisible(marker.id, false);
                continue;
            }

            // Center label above marker; extra 6px for the CSS ::after tail tip
            entry.root.style.transform = `translate(calc(${markerX.toFixed(2)}px - 50%), calc(${markerY.toFixed(2)}px - 100% - 6px))`;
            entry.root.classList.toggle('selected', marker.id === selectedId);

            // Leader line: simple vertical leader from marker screen position upward.
            // (Uses the SVG viewBox in pixel coords set above.)
            entry.leader.setAttribute('x1', markerX.toFixed(2));
            entry.leader.setAttribute('y1', markerY.toFixed(2));
            entry.leader.setAttribute('x2', markerX.toFixed(2));
            entry.leader.setAttribute('y2', (markerY - 18).toFixed(2));

            this.setLabelVisible(marker.id, true);
        }
    }

}

export { AnnotationMarkers };
